import jsLogger from 'js-logger'

const maxRetryCount = 3
const maxDelaySec = 4
const defaultTimeoutMs = 5000 // 5 seconds default timeout

/**
 * 4xx responses are permanent client errors and must not be retried — re-sending
 * the same request can never change the outcome (and for non-idempotent POSTs it
 * actively causes conflicts, e.g. "session already exists"). The exceptions are
 * 408 (Request Timeout) and 429 (Too Many Requests), which are transient.
 * Anything with no HTTP status (network failure / abort timeout) is transient too.
 */
const isRetryableStatus = (status: number): boolean =>
  status === 408 || status === 429 || status >= 500

/**
 * Performs a fetch with retry and timeout logic.
 * If timeoutMs is not provided in options, uses the defaultTimeoutMs.
 *
 * options can include:
 *  - `timeoutMs`: custom per-request timeout (overrides the default).
 *  - `maxRetries`: max retry attempts. Pass `0` to disable retries entirely —
 *    required for non-idempotent calls (e.g. session start) where a retry after a
 *    timeout would hit an already-created resource and fail with a 4xx conflict.
 */
export const fetchWithRetries = async (
  url: string,
  // eslint-disable-next-line no-undef
  options: RequestInit & { timeoutMs?: number; maxRetries?: number },
  retries: number = 1
): Promise<Response> => {
  const timeoutMs = options?.timeoutMs ?? defaultTimeoutMs
  const maxRetries = options?.maxRetries ?? maxRetryCount
  const externalSignal = options?.signal
  let timeoutController: AbortController | undefined
  let timeoutId: NodeJS.Timeout | undefined

  try {
    // If externally aborted before we even start, bail out immediately
    if (externalSignal?.aborted) {
      throw new DOMException('The operation was aborted.', 'AbortError')
    }

    timeoutController = new AbortController()

    // Abort on timeout OR external signal
    const onExternalAbort = () => timeoutController!.abort()
    externalSignal?.addEventListener('abort', onExternalAbort, { once: true })

    const {
      signal: _discarded,
      timeoutMs: _discardedTimeout,
      maxRetries: _discardedMaxRetries,
      ...restOptions
    } = options as any
    const mergedOptions: RequestInit = {
      ...restOptions,
      signal: timeoutController.signal
    }

    jsLogger.log(
      `Attempting fetch for ${url}, try ${retries}, timeout ${timeoutMs}ms`
    )

    // Set up timeout to abort the request
    timeoutId = setTimeout(() => {
      timeoutController!.abort()
    }, timeoutMs)

    const response = await fetch(url, mergedOptions)
    clearTimeout(timeoutId)
    externalSignal?.removeEventListener('abort', onExternalAbort)

    if (!response.ok) {
      let body = ''
      try {
        body = await response.text()
      } catch {}
      const reason = `HTTP ${response.status} ${response.statusText}${body ? ` — ${body}` : ''}`
      jsLogger.error(`Fetch failed for ${url} on try ${retries}: ${reason}`)
      const httpError = new Error(reason) as Error & { status?: number }
      httpError.status = response.status
      throw httpError
    }

    jsLogger.log(`Fetch successful for ${url} on try ${retries}`)
    return response
  } catch (err: any) {
    if (timeoutId) clearTimeout(timeoutId)

    // If externally aborted, propagate immediately without retrying
    if (externalSignal?.aborted) {
      throw new DOMException('The operation was aborted.', 'AbortError')
    }

    // HTTP errors carry a `status` and were already logged at the throw site
    // above. Network/timeout errors have no status — log them here.
    const status: number | undefined = err?.status
    let reason: string
    if (err && (err.name === 'AbortError' || err.code === 20)) {
      reason = `Timeout after ${timeoutMs}ms`
    } else {
      reason = err?.message || String(err)
    }
    if (status === undefined) {
      jsLogger.error(`Fetch failed for ${url} on try ${retries}: ${reason}`)
    }

    // No status → transient (network/timeout). With a status, only 408/429/5xx
    // are worth retrying; 4xx are permanent and re-sending is pointless/harmful.
    const retryable = status === undefined || isRetryableStatus(status)

    if (!retryable) {
      jsLogger.error(`Not retrying ${url}: non-retryable response. ${reason}`)
      throw err instanceof Error ? err : new Error(reason)
    }

    if (retries <= maxRetries) {
      const delay =
        Math.min(Math.pow(2, retries) / 4 + Math.random(), maxDelaySec) * 1000

      jsLogger.info(`Retrying fetch for ${url} after delay of ${(delay / 1000).toFixed(1)}s. Reason: ${reason}`)
      await new Promise((resolve) => setTimeout(resolve, delay))

      // Check external abort again after delay
      if (externalSignal?.aborted) {
        throw new DOMException('The operation was aborted.', 'AbortError')
      }

      return fetchWithRetries(url, options, retries + 1)
    } else {
      jsLogger.error(`Max retries exceeded for ${url}. Last failure: ${reason}`)
      throw new Error(`Max retries exceeded. Last failure: ${reason}`)
    }
  }
}

export default fetchWithRetries
